import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import Select from 'react-select';
import ScheduleAppointment from '../Tasks/Modals/ScheduleAppointment';
import AppointmentCalendar from '../Tasks/AppointmentsCalendar';
import BreadCrumb from '../BreadCrumb';
import { Dialog } from '@headlessui/react';



const pages = [
  { name: 'Customer Care', to: '/customers', component: Link, current: false },
  { name: 'Customer Appointments', to: '/customers/appointments', component: Link, current: true },
];

const AppointmentsManager = () => {
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [selectedVehicleForAppointment, setSelectedVehicleForAppointment] = useState(null);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [nextServiceDate, setNextServiceDate] = useState('');
  const [odometerReading, setOdometerReading] = useState('');

  useEffect(() => {
    const unsubscribeFromVehicles = onSnapshot(collection(db, 'vehicles'), (snapshot) => {
      const vehicleList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setVehicles(vehicleList);
    });

    const unsubscribeFromCustomers = onSnapshot(collection(db, 'customers'), (snapshot) => {
      const customerList = snapshot.docs.map((doc) => ({
        value: doc.id,
        label: `${doc.data().firstName} ${doc.data().lastName}`,
      }));
      setCustomers(customerList);
    });

    return () => {
      unsubscribeFromVehicles();
      unsubscribeFromCustomers();
    };
  }, []);

 

  const handleScheduleAppointment = (vehicle) => {
    setSelectedVehicleForAppointment(vehicle);
    setIsAppointmentModalOpen(true);
  };

  const handleServiceDetails = (vehicle) => {
    setSelectedVehicleForAppointment(vehicle);
    setIsServiceModalOpen(true);
  };

  const handleServiceDetailsSubmit = async () => {
    if (selectedVehicleForAppointment) {
      const vehicleRef = doc(db, 'vehicles', selectedVehicleForAppointment.id);
      await updateDoc(vehicleRef, {
        nextServiceDate: new Date(nextServiceDate),
        odometerReading: Number(odometerReading),
      });
      setIsServiceModalOpen(false);
      setNextServiceDate('');
      setOdometerReading('');
    }
  };

  const filteredVehicles = vehicles.filter(vehicle => vehicle.customerId === (selectedCustomer?.value || ''));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Schedule Appointments
          </h1>
          <p className="text-gray-600">
            Manage customer appointments and service schedules
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Panel - Customer & Vehicle Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Selection Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Select Customer
              </h2>
              <div>
                <label htmlFor="selectedCustomer" className="block text-sm font-medium text-gray-700 mb-2">
                  Customer
                </label>
                <Select
                  options={customers}
                  onChange={setSelectedCustomer}
                  value={selectedCustomer}
                  className="text-sm"
                  placeholder="Search and select a customer..."
                  isSearchable
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: '0.75rem',
                      borderColor: '#E5E7EB',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#6366F1'
                      }
                    })
                  }}
                />
              </div>
            </div>

            {/* Vehicles List */}
            <div className="space-y-4">
              {!selectedCustomer && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center">
                  <svg className="w-16 h-16 mx-auto text-indigo-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-600 font-medium">
                    Please select a customer to view their vehicles
                  </p>
                </div>
              )}

              {selectedCustomer && filteredVehicles.length === 0 && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 text-center">
                  <svg className="w-16 h-16 mx-auto text-amber-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-gray-600 font-medium">
                    No vehicles found for this customer
                  </p>
                </div>
              )}

              {filteredVehicles.map((vehicle) => (
                <div key={vehicle.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {vehicle.vehicleMake} {vehicle.vehicleModel}
                        </h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p className="flex items-center">
                            <span className="font-medium text-gray-700 w-24">Year:</span>
                            <span>{vehicle.year}</span>
                          </p>
                          <p className="flex items-center">
                            <span className="font-medium text-gray-700 w-24">VIN:</span>
                            <span className="font-mono text-xs">{vehicle.VIN}</span>
                          </p>
                          <p className="flex items-center">
                            <span className="font-medium text-gray-700 w-24">Reg. Number:</span>
                            <span className="font-semibold text-indigo-600">{vehicle.registrationNumber}</span>
                          </p>
                        </div>
                      </div>
                      <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                        </svg>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => handleScheduleAppointment(vehicle)}
                        className="flex-1 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold py-2.5 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 flex items-center justify-center"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Schedule
                      </button>
                      <button
                        type="button"
                        onClick={() => handleServiceDetails(vehicle)}
                        className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-2.5 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 flex items-center justify-center"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Service Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Calendar */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-2 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Appointment Calendar
              </h2>
              <AppointmentCalendar />
            </div>
          </div>
        </div>

        {/* Modals */}
        {isAppointmentModalOpen && (
          <ScheduleAppointment
            vehicle={selectedVehicleForAppointment}
            customer={customers.find(c => c.value === selectedVehicleForAppointment.customerId)}
            onClose={() => setIsAppointmentModalOpen(false)}
          />
        )}

        {isServiceModalOpen && (
          <Dialog
            open={isServiceModalOpen}
            onClose={() => setIsServiceModalOpen(false)}
            className="relative z-50"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Dialog.Panel className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 transform transition-all">
                <Dialog.Title className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center mr-3">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  Enter Service Details
                </Dialog.Title>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Next Service Date
                    </label>
                    <input
                      type="date"
                      value={nextServiceDate}
                      onChange={(e) => setNextServiceDate(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Odometer Reading
                    </label>
                    <input
                      type="number"
                      value={odometerReading}
                      onChange={(e) => setOdometerReading(e.target.value)}
                      placeholder="Enter odometer reading"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <button
                    type="button"
                    className="flex-1 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
                    onClick={handleServiceDetailsSubmit}
                  >
                    Save Details
                  </button>
                  <button
                    type="button"
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200"
                    onClick={() => setIsServiceModalOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default AppointmentsManager;
